import EmptyState from "@/components/EmptyState";
import type { MaterialNames } from "@/components/material_symbol_names";
import CareerPlanSummaryCard from "./_CareerPlanSummaryCard";
import {
  HUB_CAREER_PLAN_COMPLETED_LABEL,
  HUB_CAREER_PLAN_DRAFTS_LABEL,
  HUB_CAREER_PLAN_PUBLISHED_LABEL,
  summarizeHubCareerPlans,
  type HubCareerPlan
} from "./careerPlanHubSummaries";
import { careerProfilePlansHref } from "./careerPlanLatest";

const CAREER_PLAN_COUNT_ICONS = {
  published: "flag",
  drafts: "sticky_note_2",
  completed: "assignment"
} as const satisfies Record<string, MaterialNames>;

export default function CareerPlanCard({
  loading,
  pgaId,
  plans = [],
  canCreatePlan = false
}: {
  loading: boolean;
  pgaId?: string;
  plans?: HubCareerPlan[];
  canCreatePlan?: boolean;
}) {
  const { draftCount, publishedCount, completedCount, latestUpdatedAt } = summarizeHubCareerPlans(plans);
  const hasPlans = plans.length > 0;
  const createAction =
    canCreatePlan && pgaId
      ? {
          children: "Create Career Plan",
          icon: "add" as const,
          to: "/people/:pgaId/career/career-profile/onboarding" as const,
          params: { pgaId }
        }
      : undefined;

  return (
    <CareerPlanSummaryCard
      title="Career Plans"
      testId="career-plan"
      loading={loading}
      updatedAt={hasPlans ? latestUpdatedAt : undefined}
      viewAllHref={pgaId ? careerProfilePlansHref(pgaId) : undefined}
      showViewAll={hasPlans}
      options={
        hasPlans
          ? [
              {
                iconName: CAREER_PLAN_COUNT_ICONS.published,
                overline: HUB_CAREER_PLAN_PUBLISHED_LABEL,
                headline: String(publishedCount)
              },
              {
                iconName: CAREER_PLAN_COUNT_ICONS.drafts,
                overline: HUB_CAREER_PLAN_DRAFTS_LABEL,
                headline: String(draftCount)
              },
              {
                iconName: CAREER_PLAN_COUNT_ICONS.completed,
                overline: HUB_CAREER_PLAN_COMPLETED_LABEL,
                headline: String(completedCount)
              }
            ]
          : []
      }
    >
      {hasPlans ? null : (
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
          <EmptyState
            iconName="rubric"
            title="No Career Plans"
            caption="This person has not been provided a career plan yet."
            action={createAction}
          />
        </div>
      )}
    </CareerPlanSummaryCard>
  );
}
